import React, { useState, useEffect, useRef } from 'react';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import { apiCall } from '@/api/apiCall';
import { useQueue } from '@/context/QueueContext';
import ReusableKPIChartV2 from '../components/charts/ReusableKPIChartV2';
import { processParams } from '../utils/paramsHelper';
import { useNavigate } from 'react-router-dom';

const KpiChart12NEW = ({ year = "", month = "", monthNumber = "", contract = "", page = null,isRefresh = false }) => {
  const { enqueue } = useQueue();
const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
     const hasFetchedRef = useRef(false);
  

  const transformApiData = (apiData) => {
    if (!apiData?.data) {
      return [];
    }
    
    const data = apiData.data;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get the keys dynamically from page config labels
    const revenueKey = page?.labels?.revenue || 'Revenue';
    const transactionKey = page?.labels?.transaction || 'Units Rented';
    const atvKey = page?.labels?.atv || 'Price per Sq Mtr';
    const alsKey = page?.labels?.als || 'Occupancy%';
    
    // Check if the keys exist in data
    if (!data[revenueKey]) {
      console.error(`Key "${revenueKey}" not found in data`);
      return [];
    }
    
    const transformedData = [];
    
    // Get all available years from the revenue data
    const availableYears = Object.keys(data[revenueKey]).filter(key => key !== 'isPercent');
    
    // Process each year dynamically
    availableYears.forEach(yearValue => {
      months.forEach(monthName => {
        // Check if data exists for this month
        if (data[revenueKey][yearValue] && data[revenueKey][yearValue][monthName]) {
          let alsValue = 0;
          
          // Safely get ALS value
          if (data[alsKey] && data[alsKey][yearValue] && data[alsKey][yearValue][monthName]) {
            const rawAlsValue = data[alsKey][yearValue][monthName];
            
            // Check if it's a percentage string and convert
            if (data[alsKey]?.isPercent && typeof rawAlsValue === 'string') {
              alsValue = parseFloat(rawAlsValue.replace('%', ''));
            } else {
              alsValue = parseFloat(String(rawAlsValue).replace(/,/g, ''));
            }
          }
          
          // Safely parse all values
          const revenueValue = data[revenueKey][yearValue][monthName] 
            ? parseFloat(String(data[revenueKey][yearValue][monthName]).replace(/,/g, ''))
            : 0;
            
          const transactionValue = data[transactionKey] && data[transactionKey][yearValue] && data[transactionKey][yearValue][monthName]
            ? parseFloat(String(data[transactionKey][yearValue][monthName]).replace(/,/g, ''))
            : 0;
            
          const atvValue = data[atvKey] && data[atvKey][yearValue] && data[atvKey][yearValue][monthName]
            ? parseFloat(String(data[atvKey][yearValue][monthName]).replace(/,/g, ''))
            : 0;
          
          transformedData.push({
            month: `${monthName}-${yearValue}`,
            year: yearValue,
            revenue: revenueValue,
            transaction: transactionValue,
            atv: atvValue,
            als: alsValue,
          });
        }
      });
    });
    
    return transformedData;
  };

   useEffect(() => {
      if (!year || !month || !contract) return;
      if (hasFetchedRef.current) return;
    
      hasFetchedRef.current = true;
      fetchData();
    }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      
const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() => apiCall("GET", page?.api, apiParams,navigate,false,false,isRefresh));

      if (response?.success) {
        const transformed = transformApiData(response);
        setChartData(transformed);
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  // ✅ Dynamic Y-axis formatters - No decimals, just whole numbers
  // Dynamic Y-axis formatters based on page config
const yAxisFormatters = {
  left: page?.yAxisFormatters?.left 
    ? (value) => {
        // Revenue ke liye - thousands mein, without decimals
        if (value >= 1000) {
          return Math.round(value / 1000) + 'K';
        }
        return Math.round(value);
      }
    : (value) => Math.round(value).toLocaleString(),
  right: page?.yAxisFormatters?.right 
    ? (value) => Math.round(value)  // ✅ No decimals, just round number
    : (value) => Math.round(value).toLocaleString()
};

  return (
    <ReusableKPIChartV2
      chartData={chartData}
      title={page?.title}
      subtitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      colors={page?.colors}
      labels={page?.labels}
      showDataLabels={page?.showDataLabels ? true : false}
      yAxisFormatters={yAxisFormatters}
      transparentRevenueLabel={true}
      enableRevenueShadow={true}
    />
  );
};

export default KpiChart12NEW;